import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { trackApi, errMsg, getViewerId, storeViewerId } from '../../lib/trackApi';
import MemberPicker from '../../components/track/MemberPicker';
import TaskDetailDrawer from '../../components/track/TaskDetailDrawer';
import { C } from '../../components/track/trackUi';

const subTab = ({ isActive }) => ({
  padding: '6px 12px',
  borderRadius: 6,
  border: `1px solid ${isActive ? C.brandDark : '#d1d5db'}`,
  background: isActive ? C.brand : '#fff',
  color: isActive ? '#fff' : '#1f2937',
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

export function useTrack() {
  return useOutletContext();
}

export default function TrackLayout() {
  const [meta, setMeta] = useState({ boards: [], members: [], today: '' });
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [viewerId, setViewerId] = useState(() => Number(getViewerId()) || null);
  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadMeta = useCallback(async () => {
    try {
      const d = await trackApi.meta();
      if (!d?.ok) throw new Error(d?.error || '載入失敗');
      setMeta(d.data);
      setError('');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { reloadMeta(); }, [reloadMeta]);

  const changeViewer = (v) => {
    setViewerId(v);
    storeViewerId(v);
  };

  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const ctx = useMemo(() => ({
    meta,
    boards: meta.boards || [],
    members: meta.members || [],
    boardById: new Map((meta.boards || []).map((b) => [b.id, b])),
    reloadMeta,
    viewerId,
    openTask: setDrawerTaskId,
    refreshKey,
    bumpRefresh,
  }), [meta, reloadMeta, viewerId, refreshKey, bumpRefresh]);

  return (
    <div style={{ padding: '4px 2px 48px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <NavLink to="" end style={subTab}>每日追蹤</NavLink>
        <NavLink to="boards" style={subTab}>看板</NavLink>
        <NavLink to="gantt" style={subTab}>甘特圖</NavLink>
        <NavLink to="members" style={subTab}>成員</NavLink>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ color: C.muted }}>操作者</span>
          <MemberPicker members={meta.members} value={viewerId} onChange={changeViewer} placeholder="後台" />
        </div>
      </div>

      {error && <div style={{ color: C.danger, marginBottom: 10 }}>{error}</div>}
      {loaded && <Outlet context={ctx} />}

      {drawerTaskId && (
        <TaskDetailDrawer
          taskId={drawerTaskId}
          boards={meta.boards || []}
          members={meta.members || []}
          onClose={() => setDrawerTaskId(null)}
          onOpenTask={setDrawerTaskId}
          onChanged={bumpRefresh}
        />
      )}
    </div>
  );
}
